// src/lib/motivoPularCalculo.ts
//
// Lista fechada de motivos para "pular cálculo" (fatura que não vira boleto da Vigor).
//
// Até 15/09/2026 o motivo era texto livre. Em JUL e AGO/2026 só "não houve injeção"
// apareceu com mais de 10 grafias ("Não houve injeção.", "sem injeção", "Não houve
// injeção da vigor"...), o que impedia a Conferência do Faturamento de separar
// "não faturado com justificativa" de "não faturado sem justificativa". A lista abaixo
// é a do documento de controle de faturas do Financeiro, aprovada pelo Diretor de
// Operações, mais os motivos que já apareciam com frequência no texto livre.
//
// ⚠️ "Faturamento pendente" (que está no documento) ficou FORA de propósito: pular o
// cálculo tira a UC da fila de pendentes, e é justamente assim que uma UC fica sem
// faturar. Faturamento pendente não é exceção — é a UC continuar na fila, e a
// Conferência já mostra isso como "Não faturado — sem justificativa".
//
// O código vai em `faturas.motivo_pular_calculo_codigo` (CHECK na migration
// 20260915150000, ampliado pela de 07/10/2026 "pulo_calculo_injecao" — a lista abaixo
// espelha o CHECK: mudou aqui, muda lá). `motivo_pular_calculo` continua recebendo o
// texto legível, porque meia dúzia de telas e o histórico de cobrança exibem esse campo.
//
// Desde 07/10/2026, os motivos aceitos dependem da injeção da conta: ver
// src/lib/pularCalculoInjecao.ts.

export const MOTIVOS_PULAR_CALCULO = [
  { codigo: 'sem_novos_creditos', rotulo: 'Sem novos créditos no ciclo' },
  { codigo: 'cancelamento_confirmado', rotulo: 'Inativação ou cancelamento confirmado na distribuidora' },
  { codigo: 'aguardando_primeira_compensacao', rotulo: 'Aguardando a primeira compensação' },
  { codigo: 'fatura_nao_recebida', rotulo: 'Fatura não recebida' },
  { codigo: 'falha_processamento', rotulo: 'Falha de processamento' },
  { codigo: 'desconto_100_grupo', rotulo: 'Desconto de 100% aplicado à fatura do grupo' },
  { codigo: 'boleto_ja_enviado', rotulo: 'Boleto já emitido em outra fatura' },
  { codigo: 'uc_suspensa', rotulo: 'UC suspensa na distribuidora' },
  // Regra de injeção (07/10/2026): ver src/lib/pularCalculoInjecao.ts.
  { codigo: 'injecao_usina_terceiro', rotulo: 'Injeção só de usina que não é da Vigor' },
  { codigo: 'saldo_em_acordo', rotulo: 'Saldo já incluído em acordo ou parcelamento' },
  { codigo: 'conta_propria_vigor', rotulo: 'Conta da própria Vigor (usina, banco de créditos ou empresa do grupo)' },
  { codigo: 'dispensa_autorizada', rotulo: 'Dispensa autorizada' },
  { codigo: 'outro', rotulo: 'Outro' },
] as const;

export type CodigoMotivoPularCalculo = (typeof MOTIVOS_PULAR_CALCULO)[number]['codigo'];

/** "Outro" é o único em que a descrição substitui o rótulo no texto gravado. */
export const MOTIVO_PULAR_OUTRO: CodigoMotivoPularCalculo = 'outro';

/** Motivos em que a descrição é obrigatória. */
export const exigeDescricaoPularCalculo = (codigo: string | null | undefined): boolean =>
  codigo === MOTIVO_PULAR_OUTRO || codigo === 'dispensa_autorizada';

const CODIGOS = new Set<string>(MOTIVOS_PULAR_CALCULO.map((m) => m.codigo));

export const ehCodigoMotivoPularCalculo = (v: unknown): v is CodigoMotivoPularCalculo =>
  typeof v === 'string' && CODIGOS.has(v);

export const rotuloMotivoPularCalculo = (codigo: string | null | undefined): string =>
  MOTIVOS_PULAR_CALCULO.find((m) => m.codigo === codigo)?.rotulo ?? '';

/**
 * Valida a escolha. Devolve `null` quando está válida, ou a mensagem do que falta.
 */
export const validarMotivoPularCalculo = (
  codigo: string | null | undefined,
  descricao: string | null | undefined,
): string | null => {
  if (!codigo) return 'Selecione o motivo.';
  if (!ehCodigoMotivoPularCalculo(codigo)) return 'Motivo inválido.';
  if (codigo === MOTIVO_PULAR_OUTRO && !(descricao || '').trim()) {
    return 'Descreva o motivo quando a opção for "Outro".';
  }
  if (codigo === 'dispensa_autorizada' && !(descricao || '').trim()) {
    return 'Escreva por que a cobrança foi dispensada.';
  }
  return null;
};

/**
 * Texto gravado em `motivo_pular_calculo`: o rótulo, seguido da descrição quando houver.
 * Em "Outro" a descrição substitui o rótulo — "Outro — X" não ajuda quem lê o histórico.
 */
export const textoMotivoPularCalculo = (codigo: CodigoMotivoPularCalculo, descricao?: string | null): string => {
  const detalhe = (descricao || '').trim();
  if (codigo === MOTIVO_PULAR_OUTRO) return detalhe;
  const rotulo = rotuloMotivoPularCalculo(codigo);
  return detalhe ? `${rotulo} — ${detalhe}` : rotulo;
};

/** Campos a gravar em `faturas` junto com `pular_calculo_em`. */
export const camposMotivoPularCalculo = (codigo: CodigoMotivoPularCalculo, descricao?: string | null) => ({
  motivo_pular_calculo_codigo: codigo,
  motivo_pular_calculo: textoMotivoPularCalculo(codigo, descricao),
});

// A ordem importa: "Não houve injeção. Fatura referente a PREFEITURA" precisa cair em
// sem_novos_creditos antes de qualquer outra regra olhar o resto do texto.
const REGRAS_LEGADO: [RegExp, CodigoMotivoPularCalculo][] = [
  [/inje[cç]|sem\s+cr[eé]dito|n[aã]o\s+(houve|teve|recebeu)\s+cr[eé]dito/i, 'sem_novos_creditos'],
  [/100\s*%/, 'desconto_100_grupo'],
  [/j[aá]\s+(foi\s+)?enviad/i, 'boleto_ja_enviado'],
  [/suspens/i, 'uc_suspensa'],
  [/primeira\s+compensa/i, 'aguardando_primeira_compensacao'],
  [/n[aã]o\s+(foi\s+)?recebid|n[aã]o\s+chegou/i, 'fatura_nao_recebida'],
  [/inativ|cancelad|cancelament|desligad/i, 'cancelamento_confirmado'],
];

/**
 * Classifica um motivo gravado antes da lista fechada. Só serve para leitura
 * (relatórios); nunca é gravado de volta. Texto que não casa com nenhuma regra é "outro".
 */
export const classificarMotivoLegado = (texto: string | null | undefined): CodigoMotivoPularCalculo | null => {
  const t = (texto || '').trim();
  if (!t) return null;
  for (const [regex, codigo] of REGRAS_LEGADO) {
    if (regex.test(t)) return codigo;
  }
  return 'outro';
};

/** Código efetivo: o gravado, ou o classificado a partir do texto antigo. */
export const codigoMotivoEfetivo = (f: {
  motivo_pular_calculo_codigo?: string | null;
  motivo_pular_calculo?: string | null;
}): CodigoMotivoPularCalculo | null =>
  ehCodigoMotivoPularCalculo(f.motivo_pular_calculo_codigo)
    ? f.motivo_pular_calculo_codigo
    : classificarMotivoLegado(f.motivo_pular_calculo);
